import gameBoard from './gameBoard';
import castle from './castle';
import Enemy, { ENEMY_EVENT_TYPES, createEnemyEvent } from './enemy';
import Timer from './timer';
import questionGenerator from './questionGenerator';
import scoreHandler from './scoreHandler';
import DEFAULT_SETTINGS from './defaultSettings';
import { hideElement, showElement } from './domUtils';
import Engine from './engine';
import populateQuestionHistory from './questionHistory';
import sound from './sound';

const GAMESTATES = {
    MENU: 0,
    RUNNING: 1,
    GAMEOVER: 2,
    PAUSED: 3,
};

const startPage = document.querySelector('#start-page');
const gamePage = document.querySelector('#game-page');
const gameOverPage = document.querySelector('#game-over-page');
const difficultySelectPage = document.querySelector('#difficulty-select-page');
const answerForm = document.querySelector('.answer-form');
const answerInput = document.querySelector('#answer-input');
const mobileKeypad = document.querySelector('#mobile-keypad');
const mobileAnswerDisplay = document.querySelector('#mobile-answer-display');
const answerFeedback = document.querySelector('#answer-feedback');
const gradeSelect = document.querySelector('#grade-select');
const speedSelect = document.querySelector('#speed-select');
const gameTimer = document.querySelector('#game-timer');
const startButton = document.querySelector('.start-button');
const restartButton = document.querySelector('#restart-button');
const pauseButton = document.querySelector('.pause-button');
const gradeButtons = document.querySelectorAll('[data-grade]');
const speedButtons = document.querySelectorAll('[data-speed]');
const homeButton = document.querySelector('#home-button');
const musicButton = document.querySelector('#music-button');
const gameOverTitle = document.querySelector('#game-over-title');

const settings = { ...DEFAULT_SETTINGS };
const SPEED_SETTINGS = {
    slow: { enemySpeed: 18, enemySpeedIncrement: 0.5, spawnTimerMs: 4500 },
    normal: { enemySpeed: 28, enemySpeedIncrement: 1, spawnTimerMs: 3500 },
    fast: { enemySpeed: 42, enemySpeedIncrement: 1.5, spawnTimerMs: 2600 },
};
const ENEMY_FRAME_SIZE = 102;
const timers = {};
const fieldWidth = gameBoard.width - (castle.width - 70);
const engine = new Engine(update, draw);

let gameState = GAMESTATES.MENU;
let selectedEnemy = null;
let enemies = [];
let questionHistory = [];
let mobileAnswer = '';
let selectedGrade = 'grade1';
let answerLocked = false;
let answerResetTimer;

// PRIVATE FUNCTIONS

function spawnEnemy(immediate = false) {
    const spawnPoint = getRandomSpawnPoint(immediate);
    const enemy = Enemy({
        position: spawnPoint,
        speed: settings.enemySpeed,
        question: questionGenerator(settings.questionDifficulty),
        fieldWidth,
        handleSelectEnemy,
        damageCastle,
        deleteEnemy,
    });
    settings.enemySpeed += settings.enemySpeedIncrement;
    gameBoard.element.appendChild(enemy.element);
    enemies.push(enemy);
}

function getRandomSpawnPoint(immediate = false) {
    // Keep all three lanes visible on short phone boards as well as desktop.
    const keys = Object.keys(settings.SPAWN_POINTS);
    const laneIndex = Math.floor(Math.random() * keys.length);
    const point = settings.SPAWN_POINTS[keys[laneIndex]];
    const measuredHeight = gameBoard.element.clientHeight;
    const boardHeight = measuredHeight || (window.innerWidth <= 700 ? 190 : gameBoard.height);
    const usableHeight = Math.max(0, boardHeight - ENEMY_FRAME_SIZE);
    const laneY = keys.length > 1 ? usableHeight * laneIndex / (keys.length - 1) : 0;
    return { ...point, x: immediate ? 0 : point.x, y: laneY };
}

function deleteEnemy(element) {
    enemies = enemies.filter((enemy) => {
        if (enemy.element !== element) return true;

        if (selectedEnemy === enemy) {
            selectedEnemy = null;
        }

        questionHistory.push(enemy.getQuestionInfo());

        return false;
    });
}

function handleWin() {
    sound.win();
    gameOver('你赢了！');
}

function initialiseTimers() {
    // spawn enemy every 2.5 seconds
    timers.spawnTimer = Timer(settings.spawnTimerMs, spawnEnemy);

    // end game after 300000 ms (5 minutes)
    timers.gameTimer = Timer(settings.gameTimerMs, handleWin, {
        autoRestart: false,
    });
}

function handleAnswerSubmit(event) {
    event.preventDefault();
    submitAnswerValue(answerInput.value);
}

function syncMobileAnswer() {
    mobileAnswerDisplay.textContent = mobileAnswer || '输入答案';
}

function clearAnswer() {
    if (answerResetTimer) clearTimeout(answerResetTimer);
    answerResetTimer = undefined;
    answerLocked = false;
    answerFeedback.textContent = '';
    answerInput.value = '';
    mobileAnswer = '';
    answerInput.classList.remove('answer-wrong');
    mobileAnswerDisplay.classList.remove('answer-wrong');
    syncMobileAnswer();
}

function submitAnswerValue(rawValue) {
    if (answerLocked || !selectedEnemy || gameState !== GAMESTATES.RUNNING) return 'idle';

    const value = String(rawValue).trim().replace(/^0+(?=\d)/, '');
    if (value === '') return 'idle';

    const correctAnswer = selectedEnemy.question.answer.toString();
    answerFeedback.textContent = '';

    // A correct prefix is still pending: e.g. "2" while the answer is "24".
    if (value !== correctAnswer && correctAnswer.startsWith(value)) {
        answerInput.value = value;
        mobileAnswer = value;
        syncMobileAnswer();
        return 'pending';
    }

    const enemyEvent = createEnemyEvent(
        ENEMY_EVENT_TYPES.QUESTION_ANSWERED,
        value
    );
    enemyEvent.answer.isCorrect = value === correctAnswer;
    selectedEnemy.addEvent(enemyEvent);

    if (enemyEvent.answer.isCorrect) {
        sound.correct();
        selectedEnemy.handleDelete();
        selectedEnemy = null;
        scoreHandler.addPoints(settings.POINTS.CORRECT_ANSWER);
        clearAnswer();
        return 'correct';
    }

    scoreHandler.addPoints(settings.POINTS.WRONG_ANSWER);
    sound.wrong();
    answerFeedback.textContent = '答案不对，再试一次';
    answerInput.value = value;
    mobileAnswer = value;
    syncMobileAnswer();
    answerInput.classList.add('answer-wrong');
    mobileAnswerDisplay.classList.add('answer-wrong');
    answerLocked = true;
    answerResetTimer = setTimeout(() => clearAnswer(), 650);
    return 'wrong';
}

function handleMobileKeypadClick(event) {
    const button = event.target.closest('button');
    if (!button || answerLocked || !selectedEnemy || gameState !== GAMESTATES.RUNNING) return;

    if (button.dataset.key) {
        if (mobileAnswer.length < 6) {
            mobileAnswer += button.dataset.key;
            submitAnswerValue(mobileAnswer);
        }
    } else if (button.dataset.action === 'clear') {
        mobileAnswer = '';
    } else if (button.dataset.action === 'submit') {
        submitAnswerValue(mobileAnswer);
    }

    syncMobileAnswer();
}

function handleSelectEnemy(event) {
    const clickedEnemy = enemies.find(
        (enemy) => enemy.element === event.currentTarget
    );

    if (clickedEnemy === selectedEnemy) return;

    if (selectedEnemy) selectedEnemy.toggleSelect();

    clickedEnemy.toggleSelect();

    selectedEnemy = clickedEnemy;
    answerFeedback.textContent = '';
    clearAnswer();
}

function damageCastle(amount) {
    sound.castleHit();
    scoreHandler.addPoints(settings.POINTS.CASTLE_LIFE_LOST);
    castle.damage(amount, gameOver);
}

function gameOver(titleText) {
    if (titleText !== '你赢了！') sound.gameOver();
    sound.stopMusic();
    gameState = GAMESTATES.GAMEOVER;
    populateQuestionHistory(questionHistory, settings.lastAnswersToShow);
    hideElement(gamePage);
    gameOverTitle.textContent = titleText || '游戏结束';
    showElement(gameOverPage, 'flex');
}

function reset() {
    const speed = SPEED_SETTINGS[settings.speedMode];
    settings.enemySpeed = speed.enemySpeed;
    settings.enemySpeedIncrement = speed.enemySpeedIncrement;
    settings.spawnTimerMs = speed.spawnTimerMs;
    initialiseTimers();
    scoreHandler.reset();
    answerInput.value = '';
    mobileAnswer = '';
    answerFeedback.textContent = '';
    syncMobileAnswer();
    castle.setup(settings.castleStartingLives);
    enemies.forEach((enemy) => enemy.handleDelete());
    questionHistory = [];
}

function start(selectedDifficulty) {
    settings.questionDifficulty = selectedDifficulty;
    reset();
    gameState = GAMESTATES.RUNNING;
    spawnEnemy(true);
    engine.start();
}

function restart() {
    reset();
    hideElement(gameOverPage);
    showElement(gamePage, 'flex');
    gameState = GAMESTATES.RUNNING;
    spawnEnemy(true);
}

function pause() {
    gameState = GAMESTATES.PAUSED;
    answerInput.disabled = true;
    mobileKeypad.classList.add('not-clickable');
    enemies.forEach((enemy) => enemy.element.classList.add('not-clickable'));
}

function unPause() {
    gameState = GAMESTATES.RUNNING;
    answerInput.disabled = false;
    mobileKeypad.classList.remove('not-clickable');
    enemies.forEach((enemy) => enemy.element.classList.remove('not-clickable'));
}

function update(deltaTime) {
    if (gameState !== GAMESTATES.RUNNING) return;

    Object.keys(timers).forEach((key) => timers[key].tick(deltaTime));

    enemies.forEach((enemy) => enemy.update(deltaTime));
}

function draw() {
    gameTimer.textContent = timers.gameTimer.getHumanTimeRemaining();

    enemies.forEach((enemy) => enemy.draw());
}

function handlePause() {
    const pauseButtonText = ['继续', '暂停'];
    const [first, second] = pauseButtonText;
    if (gameState === GAMESTATES.RUNNING) {
        pause();
        engine.stop();
        pauseButton.textContent = first;
    } else if (gameState === GAMESTATES.PAUSED) {
        engine.start();
        unPause();
        pauseButton.textContent = second;
    }
}

function handleStartButtonClick() {
    sound.resume();
    hideElement(startPage);
    showElement(difficultySelectPage, 'flex');
    gradeSelect.style.display = 'flex';
    speedSelect.style.display = 'none';
}

function handleGradeSelect(event) {
    sound.tap();
    selectedGrade = event.currentTarget.dataset.grade;
    gradeSelect.style.display = 'none';
    speedSelect.style.display = 'flex';
}

function handleSpeedSelect(event) {
    sound.tap();
    sound.startMusic();
    settings.speedMode = event.currentTarget.dataset.speed;
    hideElement(difficultySelectPage);
    showElement(gamePage, 'flex');
    start(selectedGrade);
}

function handleHomeButtonClick() {
    if (gameState === GAMESTATES.PAUSED) handlePause();
    engine.stop();
    sound.stopMusic();
    gameState = GAMESTATES.MENU;
    window.location.assign('/');
}

// PUBLIC FUNCTIONS

function init() {
    startButton.addEventListener('click', handleStartButtonClick);
    restartButton.addEventListener('click', restart);
    pauseButton.addEventListener('click', handlePause);
    answerForm.addEventListener('submit', handleAnswerSubmit);
    answerInput.addEventListener('input', () => submitAnswerValue(answerInput.value));
    gradeButtons.forEach((button) => button.addEventListener('click', handleGradeSelect));
    speedButtons.forEach((button) => button.addEventListener('click', handleSpeedSelect));
    homeButton.addEventListener('click', handleHomeButtonClick);
    musicButton.addEventListener('click', () => {
        const playing = sound.toggleMusic();
        musicButton.textContent = playing ? '音乐开' : '音乐关';
        musicButton.setAttribute('aria-pressed', String(playing));
        musicButton.setAttribute('aria-label', playing ? '关闭背景音乐' : '开启背景音乐');
    });
    mobileKeypad.addEventListener('click', handleMobileKeypadClick);
    syncMobileAnswer();
}

export default Object.freeze({
    init,
});
